import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import iso from "iso-3166-1";
import DatePicker from "react-datepicker";
import moment from "moment";
import Select from "react-select";
import Swal from "sweetalert2";
import PhoneInput from "react-phone-number-input";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCheckSquare,
  faBriefcase,
  faFileImport,
  faClipboard,
  faCheck,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";

import Loader from "../../reusables/Loader";
import * as api from "../../../api";
import {
  regionOptions,
  yearsExperienceOptions,
  dressSizeOptions,
} from "../../../utils/signupPartnerOptions";

import "react-phone-number-input/style.css";
import "react-datepicker/dist/react-datepicker.css";
import "./signup.style.css";

const PartnerSignup = () => {
  const serverEnv = process.env.REACT_APP_SERVER_ENV || "uk";
  const isStaging = process.env.REACT_APP_NODE_URL?.includes("staging");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [signupError, setSignupError] = useState(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [practiceOptions, setPracticeOptions] = useState([]);

  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    dateOfBirth: "",
    address: "",
    city: "",
    postcode: "",
    countryCode: "GBR",
    residentialStatus: "",
    discipline: [],
    yearsOfExperience: "",
    bookingService: "",
    preferredWorkLocation: "",
    region: "",
    potentialStartDate: "",
    dressSize: "",
    notes: "",
    hasOwnVehicle: false,
    hasConfirmedToCollectAndStore: false,
    hasConfirmedReadPrivacyPolicy: false,
    workDayAvailability: {
      Monday: "",
      Tuesday: "",
      Wednesday: "",
      Thursday: "",
      Friday: "",
    },
    rightToWorkDocumentation: [],
    driversLicence: [],
    dbsPoliceCheck: [],
    cv: [],
    practicingCertificate: [],
    insuranceDocuments: [],
    vaccineStatus: [],
  });

  const [mobileNumber, setMobileNumber] = useState("");
  const [selectedDiscipline, setSelectedDiscipline] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [selectedWorkLocation, setSelectedWorkLocation] = useState([]);
  const [selectedYearsExperience, setSelectedYearsExperience] = useState([]);
  const [selectedDressSize, setSelectedDressSize] = useState([]);

  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    confirmedPassword: false,
  });

  const formSteps = {
    1: {
      fields: [
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "password",
        "dateOfBirth",
        "address",
        "city",
        "postcode",
        // "countryCode",
        "residentialStatus",
      ],
    },
    2: {
      fields: ["rightToWorkDocumentation", "driversLicence", "dbsPoliceCheck"],
    },
    3: {
      fields: [
        "discipline",
        "yearsOfExperience",
        "bookingService",
        "preferredWorkLocation",
        "region",
        "potentialStartDate",
      ],
    },
    4: {
      fields: [
        "cv",
        "practicingCertificate",
        "insuranceDocuments",
        "vaccineStatus",
      ],
    },
    5: { fields: ["dressSize"] },
    6: {
      fields: [
        "hasConfirmedToCollectAndStore",
        "hasConfirmedReadPrivacyPolicy",
      ],
    },
  };

  const validateDocumentUpload = () => {
    if (
      signupForm.cv.length <= 0 ||
      signupForm.practicingCertificate.length <= 0 ||
      signupForm.insuranceDocuments.length <= 0 ||
      signupForm.vaccineStatus.length <= 0
    ) {
      let missingField = "";

      if (signupForm.cv.length <= 0) {
        missingField = "CV";
      } else if (signupForm.practicingCertificate.length <= 0) {
        missingField = "Practicing Certificate";
      } else if (signupForm.insuranceDocuments.length <= 0) {
        missingField = "Insurance Documents";
      } else if (signupForm.vaccineStatus.length <= 0) {
        missingField = "Vaccine Status";
      }

      Swal.fire({
        icon: "error",
        title: "Missing Document",
        text: `Please upload your ${missingField}.`,
        confirmButtonColor: "#325ba6",
      });

      return false;
    }

    return true;
  };

  const calculateProgress = () => {
    const allFields = Object.values(formSteps).flatMap((step) => step.fields);
    const totalFields = allFields.length;

    // Count how many required fields are filled
    const filledFields = allFields.filter((field) => {
      const value = signupForm[field];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === "string") {
        return value.trim() !== "";
      }
      return Boolean(value);
    }).length;

    return (filledFields / totalFields) * 100;
  };

  const getPasswordErrors = () => {
    let errors = [];
    const password = signupForm.password || "";
    const confirm = confirmPassword || "";

    if (password.length < 8) {
      errors.push("Password must be at least 8 characters.");
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-\\[\]=+;'/`~]/.test(password)) {
      errors.push("Password must contain at least one special character.");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter.");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter.");
    }
    // if (!/[0-9]/.test(password)) {
    //   errors.push("Password must contain at least one number.");
    // }
    if (password && confirm && password !== confirm) {
      errors.push("Passwords do not match.");
    }
    return errors;
  };

  const passwordErrors = getPasswordErrors();
  const isPasswordInvalid = passwordErrors.length > 0 && signupForm.password;

  useEffect(() => {
    if (signupError) {
      const errors = signupError.map((err) => err.message).join("<br>");
      Swal.fire({
        title: "Error!",
        html: errors,
        icon: "error",
        confirmButtonColor: "#325ba6",
      });
    }
  }, [signupError]);

  useEffect(() => {
    const allowedAlpha3 = ["GBR", "AUS", "NZL"];
    const allCountries = iso.all?.() || [];
    if (allCountries.length > 0) {
      setCountries(
        allCountries.filter((c) => allowedAlpha3.includes(c.alpha3))
      );
    }
  }, []);

  const selectOptionCustomStyles = {
    control: (provided) => ({
      ...provided,
      border: "none",
      borderBottom: "1px solid #ced4da",
      borderRadius: 0,
      boxShadow: "none",
      "&:hover": {
        borderBottom: "1px solid #ced4da",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
  };

  const workLocationOptions = {
    uk: [{ value: "UK Region", label: "UK Region" }],
    aus: [{ value: "Australia", label: "Australia" }],
    nz: [{ value: "New Zealand", label: "New Zealand" }],
  };

  const cleanRegions = regionOptions.map((item) =>
    item.split(/ - \d+|\(\w+\s*\d*\)|\s*-\s*\d+/)[0].trim()
  );
  const uniqueRegions = Array.from(new Set(cleanRegions));
  const regionMapping = {
    Australia: ["Sydney"],
    "New Zealand": ["Auckland"],
    "UK Region": uniqueRegions.filter(
      (r) => r !== "Sydney" && r !== "Auckland"
    ),
  };

  const getRegionOptions = (workLocation) => {
    if (!workLocation) return [];

    const regions = regionMapping[workLocation.value] || [];

    return regions.map((region) => ({
      value: region,
      label: region,
    }));
  };

  useEffect(() => {
    const getPractice = async () => {
      setLoading(true);
      try {
        let practicesArray = [];
        const data = await api.getPractices();
        const practice = data.data.data;
        practice.forEach((item) => {
          practicesArray.push({
            value: item.name,
            label: item.label,
          });
        });
        setPracticeOptions(practicesArray);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getPractice();
  }, []);

  const [availability, setAvailability] = useState({
    monday: { start: "", end: "" },
    tuesday: { start: "", end: "" },
    wednesday: { start: "", end: "" },
    thursday: { start: "", end: "" },
    friday: { start: "", end: "" },
  });

  const handleAvailabilityChange = (day, field, value) => {
    setAvailability((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  const formatTime = (time) => {
    if (!time) return "";
    return moment(time, "HH:mm").format("hh:mm A");
  };

  useEffect(() => {
    const formatted = {};
    Object.entries(availability).forEach(([day, { start, end }]) => {
      const Day = day.charAt(0).toUpperCase() + day.slice(1);
      if (start && end) {
        formatted[Day] = `${formatTime(start)} - ${formatTime(end)}`;
      } else {
        formatted[Day] = "";
      }
    });
    setSignupForm((prev) => ({
      ...prev,
      workDayAvailability: formatted,
    }));
  }, [availability]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      phoneNumber: mobileNumber,
    }));
  }, [mobileNumber]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      discipline: selectedDiscipline.map((item) => item.value),
    }));
  }, [selectedDiscipline]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      region: selectedRegion?.value || "",
    }));
  }, [selectedRegion]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      preferredWorkLocation: selectedWorkLocation?.value || "",
    }));
  }, [selectedWorkLocation]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      yearsOfExperience: selectedYearsExperience?.value || "",
    }));
  }, [selectedYearsExperience]);

  useEffect(() => {
    setSignupForm((prev) => ({
      ...prev,
      dressSize: selectedDressSize?.value || "",
    }));
  }, [selectedDressSize]);

  const handleChange = (event, valueOverride) => {
    const { name, value, type, checked, files } = event.target;

    if (name === "bookingService") {
      setSignupForm({
        ...signupForm,
        bookingService:
          signupForm.bookingService === valueOverride ? "" : valueOverride,
      });
      return;
    }

    if (type === "file") {
      setSignupForm({
        ...signupForm,
        [valueOverride]: files[0] ? [files[0]] : [],
      });
      return;
    }

    setSignupForm({
      ...signupForm,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleSelectOption = (selected, name) => {
    if (name === "discipline") {
      setSelectedDiscipline(selected);
    } else if (name === "region") {
      setSelectedRegion(selected);
    } else if (name === "preferredWorkLocation") {
      setSelectedWorkLocation(selected);
    } else if (name === "yearsOfExperience") {
      setSelectedYearsExperience(selected);
    } else {
      setSelectedDressSize(selected);
    }
  };

  const imageHandleChange = (e, name) => {
    const tempFiles = Array.from(e.target.files).map((file) => file);
    if (tempFiles.filter((file) => file.size > 1000000).length > 0) {
      Swal.fire({
        html: "Can only upload files less than 1MB",
        icon: "error",
        title: "Error",
        confirmButtonColor: "#325ba6",
      });
      return;
    }
    setSignupForm({
      ...signupForm,
      [name]: tempFiles.filter((file) => file.size < 1000000),
    });
  };

  const handleChangeDate = (date, name) => {
    setSignupForm({
      ...signupForm,
      [name]: date ? moment(date).format("YYYY-MM-DD") : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateDocumentUpload()) {
      return;
    }

    setLoading(true);

    const fileFields = [
      "rightToWorkDocumentation",
      "driversLicence",
      "dbsPoliceCheck",
      "cv",
      "practicingCertificate",
      "insuranceDocuments",
      "vaccineStatus",
    ];

    try {
      const formData = new FormData();

      Object.entries(signupForm).forEach(([key, value]) => {
        if (fileFields.includes(key) && Array.isArray(value)) {
          value.forEach((file) => formData.append(key, file));
        } else if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      const data = await api.signUpPartner(formData);
      const message = data.data.message;

      if (data) {
        Swal.fire({
          html: message,
          icon: "success",
          title: "Success",
          confirmButtonColor: "#325ba6",
        });

        setSignupForm({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          password: "",
          dateOfBirth: "",
          address: "",
          city: "",
          postcode: "",
          countryCode: "GBR",
          residentialStatus: "",
          discipline: [],
          yearsOfExperience: "",
          bookingService: "",
          preferredWorkLocation: "",
          region: "",
          potentialStartDate: "",
          dressSize: "",
          notes: "",
          hasOwnVehicle: false,
          hasConfirmedToCollectAndStore: false,
          hasConfirmedReadPrivacyPolicy: false,
          workDayAvailability: {
            Monday: "",
            Tuesday: "",
            Wednesday: "",
            Thursday: "",
            Friday: "",
          },
          rightToWorkDocumentation: [],
          driversLicence: [],
          dbsPoliceCheck: [],
          cv: [],
          practicingCertificate: [],
          insuranceDocuments: [],
          vaccineStatus: [],
        });
        setMobileNumber("");
        setConfirmPassword("");
        setSelectedDiscipline([]);
        setSelectedRegion([]);
        setSelectedWorkLocation([]);
        setSelectedYearsExperience([]);
        setSelectedDressSize([]);
      }
    } catch (error) {
      setSignupError(error?.response?.data?.errors);
    } finally {
      setLoading(false);
    }
  };

  const privacyPolicyUrl = (() => {
    let env;
    switch (serverEnv) {
      case "uk":
        env = "uk";
        break;
      case "aus":
        env = "au";
        break;
      case "nz":
        env = "new-zealand";
        break;
      default:
        env = "uk";
    }
    const domain = isStaging
      ? "rapid-website-staging.azurewebsites.net"
      : "www.rapidcare4u.com";
    return `https://${domain}/${env}/privacy-policy.php`;
  })();

  const stepCards = [
    // Step 1
    <div className="card mb-4" key="step1">
      <div className="card-header orange-bg-1 text-white">
        <FontAwesomeIcon
          icon={faUser}
          style={{
            height: "15px",
            width: "15px",
            marginRight: "10px",
          }}
        />
        Step 1: Personal Information
      </div>
      <div className="card-body">
        <div className="form-group">
          <input
            type="text"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="First Name"
            name="firstName"
            value={signupForm.firstName ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <input
            type="text"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Last Name"
            name="lastName"
            value={signupForm.lastName ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <input
            type="email"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Email"
            name="email"
            value={signupForm.email ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <PhoneInput
            className="phone-input ml-2"
            international
            countryCallingCodeEditable={false}
            defaultCountry="GB"
            value={mobileNumber}
            onChange={setMobileNumber}
            placeholder="Phone Number"
          />
        </div>
        <div className="form-group" style={{ position: "relative" }}>
          <input
            type={showPassword.currentPassword ? "text" : "password"}
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: isPasswordInvalid
                ? "1px solid red"
                : "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Password"
            name="password"
            value={signupForm.password ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
          <span
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#6c757d",
              zIndex: 2,
            }}
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                currentPassword: !prev.currentPassword,
              }))
            }
            title={
              showPassword.currentPassword ? "Hide password" : "Show password"
            }
          >
            <FontAwesomeIcon
              icon={showPassword.currentPassword ? faEyeSlash : faEye}
            />
          </span>
        </div>
        <div className="form-group" style={{ position: "relative" }}>
          <input
            type={showPassword.confirmedPassword ? "text" : "password"}
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: isPasswordInvalid
                ? "1px solid red"
                : "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Confirm Password"
            name="confirmPassword"
            value={confirmPassword ?? ""}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="off"
          />
          <span
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#6c757d",
              zIndex: 2,
            }}
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                confirmedPassword: !prev.confirmedPassword,
              }))
            }
            title={
              showPassword.confirmedPassword ? "Hide password" : "Show password"
            }
          >
            <FontAwesomeIcon
              icon={showPassword.confirmedPassword ? faEyeSlash : faEye}
            />
          </span>
          {isPasswordInvalid && (
            <div
              style={{
                color: "red",
                position: "absolute",
                left: 0,
                top: "100%",
                zIndex: 1,
                background: "#fff",
                width: "100%",
                padding: "0.25rem 0.5rem",
                marginTop: "0.25rem",
                borderRadius: "0 0 4px 4px",
              }}
            >
              {passwordErrors.map((err, idx) => (
                <div key={idx}>{err}</div>
              ))}
            </div>
          )}
        </div>
        <div className="form-group d-flex flex-column">
          <DatePicker
            className="form-control datepicker-input rounded-0"
            selected={signupForm.dateOfBirth}
            onChange={(e) => handleChangeDate(e, "dateOfBirth")}
            dateFormat="dd/MM/yyyy"
            dateFormatCalendar="MMMM"
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={100}
            maxDate={new Date()}
            placeholderText="Date of Birth"
          />
        </div>

        <div className="form-group">
          <input
            type="text"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Address"
            name="address"
            value={signupForm.address ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <input
            type="text"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="City"
            name="city"
            value={signupForm.city ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <input
            type="text"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Postcode"
            name="postcode"
            value={signupForm.postcode ?? ""}
            onChange={handleChange}
            autoComplete="off"
          />
        </div>
        <div className="form-group">
          <select
            onChange={handleChange}
            className="form-control rounded-0"
            id="country"
            name="countryCode"
            value={signupForm.countryCode ?? ""}
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
          >
            <option value="" disabled>
              Country
            </option>
            {countries &&
              countries.map((data, index) => (
                <option value={data.alpha3} key={index}>
                  {data.country}
                </option>
              ))}
          </select>
        </div>
        <div className="form-group">
          <input
            type="text"
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Residential Status (Homeowner, Tenant, Living with Relatives)"
            name="residentialStatus"
            value={signupForm.residentialStatus ?? ""}
            onChange={handleChange}
          />
        </div>
      </div>
    </div>,
    // Step 2
    <div className="card mb-4" key="step2">
      <div className="card-header orange-bg-1 text-white">
        <FontAwesomeIcon
          icon={faCheckSquare}
          style={{
            height: "15px",
            width: "15px",
            marginRight: "10px",
          }}
        />
        Step 2: Work Eligibility
      </div>
      <div className="card-body">
        <p className="mb-3 font-italic">
          Please upload your documents so we can confirm your qualifications,
          verify your right to work in the
          {serverEnv === "uk"
            ? " UK"
            : serverEnv === "aus"
            ? " Australia"
            : " New Zealand"}
          , and ensure compliance.
        </p>
        {[
          {
            key: "rightToWorkDocumentation",
            label: "Upload Right to Work Documentation",
          },
          {
            key: "driversLicence",
            label: "Upload Driver’s Licence",
          },
          {
            key: "dbsPoliceCheck",
            label: "Upload DBS Police Check",
          },
        ].map((item, i) => (
          <div key={i} className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              style={{
                width: "15px",
                height: "15px",
                accentColor: "#325ba6",
                cursor: "not-allowed",
              }}
              id={item.key}
              readOnly
              checked={signupForm[item.key]?.length > 0}
            />
            <label className="form-check-label" htmlFor={item.key}>
              {item.label}
            </label>

            <input
              type="file"
              className="form-control-file my-2"
              onChange={(e) => handleChange(e, item.key)}
            />
          </div>
        ))}
      </div>
    </div>,
    // Step 3
    <div className="card mb-4" key="step3">
      <div className="card-header orange-bg-1 text-white">
        <FontAwesomeIcon
          icon={faBriefcase}
          style={{
            height: "15px",
            width: "15px",
            marginRight: "10px",
          }}
        />
        Step 3: Professional Details
      </div>
      <div className="card-body">
        <div className="form-group">
          <Select
            styles={selectOptionCustomStyles}
            placeholder="Select Discipline"
            isClearable
            isMulti
            options={practiceOptions}
            value={selectedDiscipline}
            onChange={(e) => handleSelectOption(e, "discipline")}
            isDisabled={loading}
            isLoading={loading}
          />
        </div>
        <div className="form-group">
          <Select
            styles={selectOptionCustomStyles}
            placeholder="Years of Experience"
            isClearable
            options={yearsExperienceOptions.map((item) => ({
              value: item,
              label: item,
            }))}
            value={selectedYearsExperience}
            onChange={(e) => handleSelectOption(e, "yearsOfExperience")}
          />
        </div>
        <div className="ml-4">
          <label className="d-block mb-2">Booking Service</label>

          {["In-Person", "Virtual", "Both"].map((option, i) => (
            <div className="form-check form-check-inline" key={i}>
              <input
                type="checkbox"
                name="bookingService"
                className="form-check-input"
                style={{
                  width: "15px",
                  height: "15px",
                  accentColor: "#325ba6",
                  cursor: "pointer",
                }}
                checked={signupForm.bookingService === option}
                onChange={(e) => handleChange(e, option)}
              />
              <label className="form-check-label">{option}</label>
            </div>
          ))}
        </div>
        <div className="form-group mt-3">
          <Select
            styles={selectOptionCustomStyles}
            placeholder="Preferred Work Location"
            isClearable
            options={workLocationOptions[serverEnv] || []}
            value={selectedWorkLocation}
            onChange={(e) => {
              handleSelectOption(e, "preferredWorkLocation");
              setSelectedRegion(null);
            }}
          />
        </div>
        <div className="form-group">
          <Select
            styles={selectOptionCustomStyles}
            placeholder="Region"
            isClearable
            options={getRegionOptions(selectedWorkLocation)}
            value={selectedRegion}
            onChange={(e) => handleSelectOption(e, "region")}
          />
        </div>
        <div className="form-group d-flex flex-column">
          <DatePicker
            className="form-control datepicker-input rounded-0"
            selected={signupForm.potentialStartDate}
            onChange={(e) => handleChangeDate(e, "potentialStartDate")}
            dateFormat="dd/MM/yyyy"
            minDate={moment().add(14, "days").toDate()}
            placeholderText="Potential Start Date"
          />
        </div>
      </div>
    </div>,
    // Step 4
    <div className="card mb-4" key="step4">
      <div className="card-header orange-bg-1 text-white">
        <FontAwesomeIcon
          icon={faFileImport}
          style={{
            height: "15px",
            width: "15px",
            marginRight: "10px",
          }}
        />
        Step 4: Document Uploads
      </div>
      <div className="card-body">
        <p className="mb-3 font-italic">
          Please upload your documents so we can confirm your qualifications,
          verify your right to work in the
          {serverEnv === "uk"
            ? " UK"
            : serverEnv === "aus"
            ? " Australia"
            : " New Zealand"}
          , and ensure compliance.
        </p>
        {[
          { key: "cv", label: "Upload CV (Resume)" },
          {
            key: "practicingCertificate",
            label: "Upload Practicing Certificate",
          },
          {
            key: "insuranceDocuments",
            label: "Upload Insurance Documents",
          },
          { key: "vaccineStatus", label: "Upload Vaccine Status" },
        ].map((item, i) => (
          <div key={i} className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              style={{
                width: "15px",
                height: "15px",
                accentColor: "#325ba6",
                cursor: "not-allowed",
              }}
              id={item.key}
              readOnly
              checked={signupForm[item.key]?.length > 0}
            />
            <label className="form-check-label" htmlFor={item.key}>
              {item.label}
            </label>

            <input
              type="file"
              className="form-control-file my-2"
              onChange={(e) => {
                const file = e.target.files[0];
                setSignupForm((prev) => ({
                  ...prev,
                  [item.key]: file ? [file] : [],
                }));
              }}
            />
          </div>
        ))}
      </div>
    </div>,
    // Step 5
    <div className="card mb-4" key="step5">
      <div className="card-header orange-bg-1 text-white">
        <FontAwesomeIcon
          icon={faClipboard}
          style={{
            height: "15px",
            width: "15px",
            marginRight: "10px",
          }}
        />
        Step 5: Additional Info
      </div>
      <div className="card-body">
        <div className="form-group">
          <Select
            styles={selectOptionCustomStyles}
            placeholder="Dress Size (For Scrubs)"
            isClearable
            options={dressSizeOptions.map((item) => ({
              value: item,
              label: item,
            }))}
            value={selectedDressSize}
            onChange={(e) => handleSelectOption(e, "dressSize")}
          />
        </div>
        <div className="form-group">
          <textarea
            className="form-control rounded-0"
            style={{
              border: "none",
              borderBottom: "1px solid #ced4da",
              boxShadow: "none",
            }}
            placeholder="Notes / Other Information"
            name="notes"
            value={signupForm.notes ?? ""}
            onChange={handleChange}
            autoComplete="off"
          ></textarea>
        </div>
        <div className="form-check">
          <input
            type="checkbox"
            name="hasOwnVehicle"
            className="form-check-input"
            style={{
              width: "15px",
              height: "15px",
              accentColor: "#325ba6",
              cursor: "pointer",
            }}
            checked={signupForm.hasOwnVehicle}
            onChange={handleChange}
          />
          <label className="form-check-label">Access to Own Vehicle</label>
        </div>
        <div>
          <p className="font-weight-bold my-3">
            Please select your availability to work.
          </p>
          <div className="d-flex justify-content-center">
            <table
              className="table-borderless text-center"
              style={{ border: "none" }}
            >
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(availability).map(([day, { start, end }]) => (
                  <tr key={day}>
                    <td className="text-capitalize">{day}</td>
                    <td>
                      <input
                        type="time"
                        value={start}
                        onChange={(e) =>
                          handleAvailabilityChange(day, "start", e.target.value)
                        }
                        className="form-control"
                      />
                    </td>
                    <td>
                      <input
                        type="time"
                        value={end}
                        onChange={(e) =>
                          handleAvailabilityChange(day, "end", e.target.value)
                        }
                        className="form-control"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>,
    // Step 6
    <div className="card mb-4" key="step6">
      <div className="card-header orange-bg-1 text-white">
        <FontAwesomeIcon
          icon={faCheck}
          style={{
            height: "15px",
            width: "15px",
            marginRight: "10px",
          }}
        />
        Step 6: Review & Consent
      </div>
      <div className="card-body">
        <div className="form-check">
          <input
            type="checkbox"
            name="hasConfirmedToCollectAndStore"
            checked={signupForm.hasConfirmedToCollectAndStore}
            onChange={handleChange}
            className="form-check-input"
            style={{
              width: "15px",
              height: "15px",
              accentColor: "#325ba6",
            }}
          />
          <label className="form-check-label" htmlFor="consent-sm">
            I confirm that the information provided is accurate and consent to
            the collection and storage of my documents in line with applicable
            data protection laws.
          </label>
        </div>
        <div className="form-check">
          <input
            type="checkbox"
            name="hasConfirmedReadPrivacyPolicy"
            checked={signupForm.hasConfirmedReadPrivacyPolicy}
            onChange={handleChange}
            className="form-check-input"
            style={{
              width: "15px",
              height: "15px",
              accentColor: "#325ba6",
            }}
          />
          <label className="form-check-label" htmlFor="consent-sm">
            I confirm that I have read the RapidCare4U{" "}
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </label>
        </div>
      </div>
    </div>,
  ];

  if (loading)
    return (
      <div className="my-3">
        <Loader />
      </div>
    );

  return (
    <>
      <div
        className="container bg-light mt-3 py-4"
        style={{ margin: "150px auto" }}
      >
        <div
          style={{ cursor: "pointer", color: "#325ba6" }}
          onClick={() => navigate(-1)}
        >
          <p>&lt; Back</p>
        </div>
        <h2
          className="text-center mb-4 font-weight-bold"
          style={{
            fontFamily: "Calibri",
            fontSize: "18pt",
          }}
        >
          Partner Sign Up Form
        </h2>

        {/* Progress Bar */}
        <div className="mb-4 justify-content-center mx-auto text-center">
          <div className="progress mx-auto w-25" style={{ height: "10px" }}>
            <div
              className="progress-bar"
              style={{
                width: `${Math.round(calculateProgress())}%`,
                backgroundColor: "#325ba6",
              }}
              role="progressbar"
              aria-valuenow={Math.round(calculateProgress())}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>

          <div
            className="font-weight-bold mt-2"
            style={{ fontFamily: "Calibri", fontSize: "11pt" }}
          >
            Progress:
            <span
              className="ml-1"
              style={{
                display: "inline-block",
                width: "40px",
                height: "15px",
                marginRight: "6px",
                backgroundColor: "#325ba6",
                borderRadius: "2px",
                verticalAlign: "middle",
              }}
            ></span>
            {Math.round(calculateProgress())}%
          </div>
        </div>

        {/* small screens */}
        <div
          className="d-block d-md-none"
          style={{
            fontFamily: "Calibri",
            fontSize: "11pt",
          }}
        >
          {stepCards}

          {/* Submit Button */}
          <div className="text-center w-100 mt-5">
            <button
              className="btn btn-primary btn-lg w-100"
              style={{ backgroundColor: "#325ba6" }}
              disabled={
                !signupForm.hasConfirmedToCollectAndStore ||
                !signupForm.hasConfirmedReadPrivacyPolicy ||
                isPasswordInvalid
              }
              onClick={handleSubmit}
            >
              Submit Application
            </button>
          </div>
        </div>

        {/* md and up screens */}
        <div
          className="d-none d-md-flex row"
          style={{
            fontFamily: "Calibri",
            fontSize: "11pt",
          }}
        >
          {/* LEFT COLUMN */}
          <div className="col-md-6">{stepCards.slice(0, 3)}</div>
          {/* RIGHT COLUMN */}
          <div className="col-md-6">
            {stepCards.slice(3)}

            {/* Submit Button */}
            <div className="text-center w-100 mt-5 d-none d-md-block">
              <button
                className="btn btn-primary btn-lg w-100"
                style={{ backgroundColor: "#325ba6" }}
                disabled={
                  !signupForm.hasConfirmedToCollectAndStore ||
                  !signupForm.hasConfirmedReadPrivacyPolicy ||
                  isPasswordInvalid
                }
                onClick={handleSubmit}
              >
                Submit Application
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PartnerSignup;
